import { CheckCircle2, Link2, RefreshCw, Unlink } from 'lucide-react'
import { useState } from 'react'

import { Button, ResourceSelectField, TextField } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { newUuid } from '../../../shared/ids'
import { QueryState } from '../../../shared/QueryState'
import '../../../shared/platform.css'
import type { CatalogLinksActions, CatalogLinksVm, IdentityRowVm, LinkedProductVm } from '../model/types'

const NOT_CHOSEN = '— не выбрано —'

/** Разрыв одной связи — двумя нажатиями и с причиной: она уходит в журнал связей. */
function IdentityRow({
  product,
  identity,
  busy,
  onUnlink,
}: {
  product: LinkedProductVm
  identity: IdentityRowVm
  busy: boolean
  onUnlink: (reason: string, operationId: string) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')
  // Ключ операции — намерение: повтор того же разрыва (обрыв ответа) — тот же ключ, правка причины — новый.
  const [operationId, setOperationId] = useState<string | null>(null)
  return (
    <li>
      <span>
        {identity.resourceLabel}: <code>{identity.remoteKey}</code> · {identity.originLabel}
      </span>{' '}
      {confirming ? (
        <span role="group" aria-label={`Разрыв связи ${product.name} — ${identity.resourceLabel}`}>
          <TextField
            label="Причина разрыва"
            value={reason}
            onChange={(value) => {
              setReason(value)
              setOperationId(null)
            }}
          />
          <Button
            variant="primary"
            icon={<Unlink size={16} aria-hidden="true" />}
            disabled={reason.trim() === '' || busy}
            busy={busy}
            onClick={() => {
              const key = operationId ?? newUuid()
              setOperationId(key)
              onUnlink(reason.trim(), key)
            }}
            actionId="platform.links.unlink"
            disabledReason={reason.trim() === '' ? 'Напишите причину разрыва — она попадёт в журнал связей.' : null}
          >
            Подтвердить разрыв
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)} actionId="platform.links.unlink">
            Не разрывать
          </Button>
        </span>
      ) : (
        <Button
          variant="ghost"
          icon={<Unlink size={16} aria-hidden="true" />}
          onClick={() => setConfirming(true)}
          actionId="platform.links.unlink"
        >
          Разорвать связь
        </Button>
      )}
    </li>
  )
}

/**
 * Окно «Связи товаров» (FIX-A2): карточки разных ресурсов (1С, AQSI) — один
 * общий товар Platform. Это вход синхронизации: правило доставляет изменение
 * карточки источника в карточку получателя, связанную с тем же товаром. Окно не
 * знает видов ресурсов: ключ карточки — строка ресурса (Ref_Key 1С, id товара AQSI).
 */
export function CatalogLinksScreen({ vm, actions }: { vm: CatalogLinksVm; actions: CatalogLinksActions }) {
  const draft = vm.draft
  return (
    <section aria-labelledby="platform-links-title" className="platform-sync">
      <header className="platform-sync__toolbar">
        <h2 id="platform-links-title">Связи товаров</h2>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.links.reload">
          Обновить
        </Button>
      </header>
      <p className="platform-sync__note">
        Здесь видно и задаётся, какие карточки разных ресурсов — один и тот же товар: например, номенклатура 1С и товар
        кабинета AQSI. Связь появляется сама, когда обмен создаёт карточку товара в получателе, или ставится вручную
        формой ниже. Обмен передаёт изменения карточки только между связанными карточками; совпадение
        названий или штрихкодов связью не считается. Разрыв связи останавливает обмен этой парой и попадает в журнал с
        причиной; сами карточки в 1С и AQSI не удаляются. К правам ресурсов этот экран отношения не имеет.
      </p>
      <QueryState query={vm.resources} emptyText="В реестре нет ресурсов" onRetry={actions.reload} />

      {vm.resources.status === 'ready' && (
        <>
          <form
            aria-label="Связать карточки"
            className="platform-sync__form"
            onSubmit={(event) => {
              event.preventDefault()
              if (vm.canLink) actions.link()
            }}
          >
            <h3>Связать карточки</h3>
            <ResourceSelectField
              label="Ресурс первой карточки"
              value={draft.firstResourceId}
              options={vm.choices}
              notChosen={NOT_CHOSEN}
              onChange={(value) => actions.patchDraft({ firstResourceId: value })}
              actionId="platform.links.first-resource"
            />
            <TextField label="Ключ первой карточки" value={draft.firstKey} onChange={(value) => actions.patchDraft({ firstKey: value })} />
            <ResourceSelectField
              label="Ресурс второй карточки"
              value={draft.secondResourceId}
              options={vm.choices}
              notChosen={NOT_CHOSEN}
              onChange={(value) => actions.patchDraft({ secondResourceId: value })}
              actionId="platform.links.second-resource"
            />
            <TextField
              label="Ключ второй карточки"
              value={draft.secondKey}
              onChange={(value) => actions.patchDraft({ secondKey: value })}
            />
            <TextField
              label="Название общего товара"
              value={draft.productName}
              placeholder="нужно, если карточки ещё не связаны"
              onChange={(value) => actions.patchDraft({ productName: value })}
            />
            <TextField label="Причина связывания" value={draft.reason} onChange={(value) => actions.patchDraft({ reason: value })} />
            <Button
              type="submit"
              variant="primary"
              icon={<Link2 size={16} aria-hidden="true" />}
              disabled={!vm.canLink}
              busy={vm.linkAction.busy}
              actionId="platform.links.link"
              disabledReason={vm.linkBlocker}
            >
              Связать
            </Button>
            {vm.linkBlocker !== null && <p className="platform-sync__reason">Связать нельзя: {vm.linkBlocker}</p>}
            {vm.linkOutcome !== null && (
              <p role="status" className="platform-sync__note">
                <CheckCircle2 size={16} aria-hidden="true" /> {vm.linkOutcome}
              </p>
            )}
            <ActionError error={vm.linkAction.error} onReload={actions.reload} />
          </form>

          <h3>Связанные товары ресурса</h3>
          <ResourceSelectField
            label="Ресурс списка"
            value={vm.listResourceId}
            options={vm.choices}
            notChosen={NOT_CHOSEN}
            onChange={actions.chooseListResource}
            actionId="platform.links.list-resource"
          />
          {vm.listResourceId === '' ? (
            <p className="platform-sync__note">Выберите ресурс, чтобы увидеть его связанные товары.</p>
          ) : (
            <QueryState query={vm.list} emptyText="У ресурса нет связанных товаров" onRetry={actions.reload} />
          )}
          <ActionError error={vm.unlinkAction.error} onReload={actions.reload} />
          {vm.list.status === 'ready' && (
            <ul className="platform-sync__list" aria-label="Связанные товары">
              {vm.products.map((product) => (
                <li key={product.id} className="platform-sync__card" aria-label={product.name}>
                  <strong>{product.name}</strong>
                  <ul aria-label={`Карточки товара ${product.name}`}>
                    {product.identities.map((identity) => (
                      <IdentityRow
                        key={identity.resourceId}
                        product={product}
                        identity={identity}
                        busy={vm.unlinkAction.busy}
                        onUnlink={(reason, operationId) => actions.unlink(product.id, identity.resourceId, reason, operationId)}
                      />
                    ))}
                  </ul>
                  {product.hiddenNote !== null && <p className="platform-sync__note">{product.hiddenNote}</p>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
